import { Dayjs } from "dayjs"
import { DateRangeViewEvent, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"

import { EventDisplayInfo } from "../scheduleModel"
import { WeekSpanningLayout, SpanningEventSegment, calculateReservedHeight, hasSpanningEventOnDay, mergePreviewIntoRowsPerDay, MONTH_VIEW_SPANNING_ROW_HEIGHT, SPANNING_EVENT_GAP, MONTH_VIEW_DAY_HEADER_HEIGHT } from "./spanningEventHelpers"
import MonthViewSpanningEvent, { ResizePreview } from "./MonthViewSpanningEvent"
import MonthViewSpanningTask from "./MonthViewSpanningTask"
import MonthViewDay from "./MonthViewDay"
import { getEventCardColors } from "../scheduleEventColorsModel"

export type Props = {
  week: Dayjs[]
  weekIndex: number
  currentMonth: number
  selectedDate: Dayjs
  eventsByDay: Map<string, DateRangeViewEvent[]>
  consultationsByDay?: Map<string, ConsultationCalendarEvent[]>
  tasksByDay?: Map<string, TaskCalendarEvent[]>
  eventDisplayInfo: Map<number, EventDisplayInfo>
  spanningLayout: WeekSpanningLayout
  previewSegments?: SpanningEventSegment[]
  onDateChange: (date: Dayjs) => void
  onEventClick: (event: DateRangeViewEvent) => void
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onTaskClick: (event: TaskCalendarEvent) => void
  onSpanningEventClick: (eventId: number) => void
  onSpanningEventResize?: (event: DateRangeViewEvent, newEndDate: Dayjs) => void
  onSpanningTaskResize?: (task: TaskCalendarEvent, newEndDate: Dayjs) => void
  onResizingChange?: (isResizing: boolean) => void
  onResizePreview?: (preview: ResizePreview) => void
  onDayScheduleClick: (date: Dayjs) => void
  onOpenOptimize: (date: Dayjs) => void
}

const MonthViewWeekRow = ({
  week,
  currentMonth,
  selectedDate,
  eventsByDay,
  consultationsByDay,
  tasksByDay,
  eventDisplayInfo,
  spanningLayout,
  previewSegments,
  onDateChange,
  onEventClick,
  onConsultationClick,
  onTaskClick,
  onSpanningEventClick,
  onSpanningEventResize,
  onSpanningTaskResize,
  onResizingChange,
  onResizePreview,
  onDayScheduleClick,
  onOpenOptimize,
}: Props) => {
  const weekStart = week[0]

  const effectiveRowsPerDay = mergePreviewIntoRowsPerDay(spanningLayout.rowsPerDay, previewSegments)

  return (
    <div
      style={{
        display: "flex",
        minHeight: 200,
        flexShrink: 0,
        position: "relative",
      }}
    >
      {spanningLayout.segments.map((segment) => (
        <MonthViewSpanningEvent
          key={`${segment.event.id}-${segment.weekIndex}`}
          segment={segment}
          weekStart={weekStart}
          eventDisplayInfo={eventDisplayInfo}
          onEventClick={onSpanningEventClick}
          onResize={onSpanningEventResize}
          onResizingChange={onResizingChange}
          onResizePreview={onResizePreview}
        />
      ))}
      {spanningLayout.taskSegments.map((segment) => (
        <MonthViewSpanningTask
          key={`spanning-task-${segment.task.id}-${segment.weekIndex}`}
          segment={segment}
          weekStart={weekStart}
          onTaskClick={onTaskClick}
          onResize={onSpanningTaskResize}
          onResizingChange={onResizingChange}
        />
      ))}
      {previewSegments?.map((segment) => {
        const { backgroundColor, palette } = getEventCardColors(segment.event.crewForemanColor)
        const leftPercent = (segment.startDayIndex / 7) * 100
        const widthPercent = (segment.spanWidth / 7) * 100
        const top = MONTH_VIEW_DAY_HEADER_HEIGHT + segment.rowIndex * (MONTH_VIEW_SPANNING_ROW_HEIGHT + SPANNING_EVENT_GAP)
        const paddingLeft = segment.isStart ? 2 : 0
        const paddingRight = segment.isEnd ? 2 : 0

        return (
          <div
            key={`preview-${segment.event.id}-${segment.weekIndex}`}
            style={{
              position: "absolute",
              top,
              left: `calc(${leftPercent}% + ${paddingLeft}px)`,
              width: `calc(${widthPercent}% - ${paddingLeft + paddingRight}px)`,
              height: MONTH_VIEW_SPANNING_ROW_HEIGHT,
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "0 8px",
              borderRadius: segment.isStart && segment.isEnd
                ? 4
                : segment.isStart
                  ? "4px 0 0 4px"
                  : segment.isEnd
                    ? "0 4px 4px 0"
                    : 0,
              backgroundColor,
              color: palette.primaryText,
              opacity: 0.5,
              overflow: "hidden",
              whiteSpace: "nowrap",
              boxSizing: "border-box",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            {!segment.isStart && (
              <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0 }}>◀</span>
            )}
            <span
              style={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                minWidth: 0,
              }}
            >
              {segment.event.jobName}
            </span>
            {!segment.isEnd && (
              <span style={{ color: palette.secondaryText, fontSize: 10, flexShrink: 0 }}>▶</span>
            )}
          </div>
        )
      })}

      {week.map((day, dayIndex) => {
        const dateKey = day.format("YYYY-MM-DD")
        const dayEvents = eventsByDay.get(dateKey) ?? []
        const dayConsultations = consultationsByDay?.get(dateKey) ?? []
        const dayTasks = tasksByDay?.get(dateKey) ?? []
        const isCurrentMonth = day.month() === currentMonth
        const isSelected = day.isSame(selectedDate, "day")
        const hasEvents = dayEvents.length > 0 || hasSpanningEventOnDay(spanningLayout.segments, dayIndex)
        const dayReservedHeight = calculateReservedHeight(effectiveRowsPerDay[dayIndex], MONTH_VIEW_SPANNING_ROW_HEIGHT)

        return (
          <MonthViewDay
            key={dateKey}
            date={day}
            events={dayEvents}
            consultations={dayConsultations}
            tasks={dayTasks}
            hasEvents={hasEvents}
            eventDisplayInfo={eventDisplayInfo}
            isCurrentMonth={isCurrentMonth}
            isSelected={isSelected}
            onDayClick={onDateChange}
            onEventClick={onEventClick}
            onConsultationClick={onConsultationClick}
            onTaskClick={onTaskClick}
            onDayScheduleClick={onDayScheduleClick}
            reservedTopHeight={dayReservedHeight}
            onOpenOptimize={onOpenOptimize}
          />
        )
      })}
    </div>
  )
}

export default MonthViewWeekRow
